/**
 * A record with nothing on it — `docs/quiet-records.md`, mockups
 * `41-quiet-records`.
 *
 * A new property, a contact with no work, a job that is still a request: the
 * middle of Overview said "Nothing here yet" and offered one button. The page
 * knew a great deal about what to do next and said none of it.
 *
 * This file is **what quiet means, the shape a record declares, and the panel
 * that draws it**. The per-record configs live with their record pages, which
 * is the same division `standardTabs` draws: the engine knows a step has a
 * glyph, a line and something it does; only a property knows that its first
 * step is a job.
 *
 * Three things here are load-bearing and easy to undo by accident:
 *
 * **Columns come from the panel's own width**, measured with a
 * `ResizeObserver` on the grid's box — never from the window (§4). The panel
 * is the main column of a record page, so the window is 400px wider than it
 * on a desktop and 280 wider on an iPad, and a media query would put three
 * tiles in a 780px column and two in a 430px one.
 *
 * **The composer is the real `ActivityComposer`** (§1 rule 7). The first note
 * somebody writes is what flips this page to the busy Overview, so it has to
 * be the same write, the same endpoint and the same summary reload.
 *
 * **The whole panel is a drop target** (§1 rule 8), through the one
 * `useRecordUpload` the Files tab uses. A page that writes its own drop
 * handler is the defect that hook exists to prevent.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import type { ActivityKind, FieldIcon, RecordType } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { usePhone } from '../lib/use-narrow.js'
import {
  COLUMN_ICONS, IconCheck, IconChevronRight, NAV_ICONS, RECORD_TAB_ICONS,
} from '../frame/icons.js'
import type { MenuItem } from '../components/context-menu.js'
import { RecordCard } from './record-rail.js'
import { ActivityComposer } from './record-activity.js'
import { useActivityPost } from './use-activity-post.js'
import type { RecordUpload } from './use-record-upload.js'
import { DescribeFile } from './record-files.js'
import { AttachSheet, QuietComposerTrigger, QuietLogSheet } from './record-quiet-sheets.js'
import { messageKinds, useCanMessage } from './record-message.js'
import type { RecordSummary } from '../lib/use-record.js'
import type { RecordTab } from './record-tabs.js'

/**
 * The same name a tab's icon is given.
 *
 * `RecordTab['icon']` already means "a key into one of the icon sets", and a
 * step's glyph is drawn from the same sets — so this is that type under a
 * name that reads right on a step, rather than a second union that would
 * have to be kept level with it.
 */
export type QuietIcon = NonNullable<RecordTab['icon']>

/**
 * Is this record quiet?
 *
 * **The condition is the one `StandardOverview` already computed**, lifted
 * out whole rather than restated (§1 rule 1): no activity, no files, and
 * every related list the record declares at zero. A second definition is a
 * page that draws the panel and the busy layout on different days.
 *
 * **`summary === null` is loading, never quiet.** That distinction is the
 * whole reason this takes a nullable summary and answers `false` for it.
 * The old expression was `!summary || (…counts…)`, which made `empty` true
 * while loading and was then guarded by `empty && summary` at the call site
 * — so a record that was still loading fell through to the *busy* layout and
 * drew its cards with nothing in them for as long as the request took. With
 * a panel in play that flash would be the panel, on every record, including
 * the ones that are not remotely quiet.
 *
 * `related` is the record's own array — the same one `standardTabs` was
 * given. It matters: `counts.jobs` on a *job* is 1 (itself), so a predicate
 * that read every count in the summary would decide no job is ever quiet.
 */
export function isQuietSummary(
  summary: RecordSummary | null,
  related: { area: string }[],
): boolean {
  if (!summary) return false
  const counts = summary.counts ?? {}
  return (counts.activity ?? 0) === 0
    && (counts.files ?? 0) === 0
    && related.every((spec) => (counts[spec.area] ?? 0) === 0)
}

/**
 * One first step.
 *
 * **A step does something** (§1 rule 2): it creates a record, opens a flyout
 * or a dialog, opens a rail card for editing, opens a tab, or opens the file
 * picker. It is never a tutorial, a tip or a line of encouragement — if the
 * only thing a tile can do is explain, it is not a step and it does not go
 * on the panel.
 */
export interface QuietStep {
  /** Stable across renders and records: 'job', 'contact', 'map', 'files'… */
  key: string
  icon: QuietIcon
  title: string
  /** What the step does, one line. */
  body: string
  /**
   * `todo` is shown and clickable, `done` is shown quiet at the end, and
   * **`absent` is not rendered at all** (§1 rule 4). A step the viewer
   * cannot take, or whose feature is off, is absent rather than greyed: a
   * disabled control invites somebody to work out why, and the honest answer
   * — "this is not yours to do" — is better said by its not being there.
   */
  state: 'todo' | 'done' | 'absent'
  /** Shown instead of `body` once done: "Santos Home", "Derek and 1 more". */
  doneBody?: string
  /**
   * Shown instead of `title` once done, where the board words it as what is
   * true rather than what to do — a job's *Contacts added* (`quiet-records.md`
   * §7, Board 3), not *Add a contact* with a tick beside it.
   */
  doneTitle?: string
  /**
   * What pressing it does.
   *
   * **Omitted on the files tile**, whose gesture belongs to the engine: a
   * picker on a desktop, the attach sheet on a phone (§4). A record cannot
   * supply it — the picker is the panel's own hidden `<input>`, which does
   * not exist until the panel renders — and a config that passed something
   * here would be passing the Files tab's opener, which on a quiet record
   * has never mounted and does nothing at all.
   */
  onSelect?: () => void
  /**
   * The dashed drop tile.
   *
   * At most one per record and **always the last one still to do** (§1 rule
   * 6), because attaching a file is the step that is never the point: a
   * record whose first suggestion is "upload something" has not been read.
   */
  kind?: 'files'
  /**
   * The step aims the composer rather than going anywhere.
   *
   * A contact's **Send a message** is the only one so far: texting somebody
   * is not a screen, it is the composer with Text picked — and on a quiet
   * page the composer is already there, two inches above the step. Sending
   * somebody to a different screen to do a thing this one does is the shape
   * this whole feature exists to replace.
   *
   * On a phone it opens the log sheet on that kind, which is the same move.
   */
  composerKind?: ActivityKind
  /**
   * What it says on a phone, where the gesture itself is different.
   *
   * Only the files tile uses it, and only because "drop photos, contracts or
   * plans anywhere on this page" is a sentence about a mouse. Everything else
   * keeps one set of words at every width (§1 rule 11, "same copy").
   */
  phone?: { title: string; body: string }
}

/** What a record page declares so its quiet page can be drawn. */
export interface QuietConfig {
  /** "Nothing is connected to Santos Home yet" */
  headline: string
  sub: string
  /** "Write a note about {composerSubject}…", and the Sheet's subtitle. */
  composerSubject: string
  /** "Set up {setupSubject}" — the Set up card's title. */
  setupSubject: string
  /** In priority order. The first one still to do leads, and leads alone. */
  steps: QuietStep[]
  /** Ghost cards under the steps: what will fill in. Three at most. */
  ghosts?: { title: string; line: string; icon: QuietIcon }[]
  /**
   * False for a read-only viewer: no composer, no steps, one plain sentence
   * (§9, "Read-only viewer"). Somebody who cannot write should not be handed
   * six things to do.
   */
  canWrite: boolean
}

/** What `RecordConfig.quiet` holds. */
export interface QuietSpec<T> {
  /** The same answer the Overview computes; the phone layout needs it too. */
  when: (record: T, summary: RecordSummary | null) => boolean
  /**
   * The summary comes in too, and it is not a convenience.
   *
   * A record's payload says what the record *is*; the summary says what is
   * **connected** to it — how many files, how many maps — and several steps
   * are done exactly when one of those counts is above nought. Without it
   * every config has to guess, and the guess is always "not done", which is
   * how **Attach files** ends up still being offered on a record that has
   * forty of them and how a Set up card outlives the thing it was setting up.
   */
  build: (record: T, summary: RecordSummary | null) => QuietConfig
}

/** Steps still to do, done, and the one that leads — counted in one place. */
export function quietProgress(config: QuietConfig): {
  todo: QuietStep[]
  done: QuietStep[]
  lead: QuietStep | null
  total: number
} {
  const shown = config.steps.filter((step) => step.state !== 'absent')
  const todo = shown.filter((step) => step.state === 'todo')
  const done = shown.filter((step) => step.state === 'done')
  /*
   * **At most six** (§1 rule 6), and the files tile is held back to last
   * among what is left rather than sorted by key: a record declares its steps
   * in priority order and that order is the answer, except for this one.
   */
  const ordered = [
    ...todo.filter((step) => step.kind !== 'files'),
    ...todo.filter((step) => step.kind === 'files'),
  ].slice(0, 6)
  return {
    todo: ordered,
    done,
    lead: ordered[0] ?? null,
    /* Out of what is left, never out of what was declared (§1 rule 4). */
    total: ordered.length + done.length,
  }
}

/**
 * How many tiles fit, from the panel's own width — §4.
 *
 * ≥ 960 three, 560–959 two, below that one card of rows. The thresholds are
 * the panel's, not the viewport's: a 1600px desktop gives the main column
 * about 1160, a 1180px iPad in landscape about 780 and the same iPad in
 * portrait about 430, so the three answers fall out of one measurement
 * instead of three breakpoints that each have to know what the rail is doing.
 */
export function quietColumns(width: number): 1 | 2 | 3 {
  if (width >= 960) return 3
  if (width >= 560) return 2
  return 1
}

/**
 * The width of a box, as it actually is.
 *
 * `ResizeObserver` rather than `window.innerWidth`, for the reason in the
 * file header. It starts at 0 — which `quietColumns` reads as one column, the
 * rows card — so the first paint is the narrow layout and the observer widens
 * it on the same frame. That order is deliberate: the rows card at a desktop
 * width for one frame is a tall list; three tiles at 390px is a horizontal
 * scrollbar.
 *
 * `ResizeObserver` is absent under jsdom, so this answers 0 there and the
 * tests reach for `quietColumns` directly, which is the part worth asserting.
 */
function useBoxWidth(box: { current: HTMLElement | null }): number {
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const node = box.current
    if (!node || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width
      if (measured !== undefined) setWidth(measured)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [box])

  return width
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

export interface QuietOverviewProps {
  quiet: QuietConfig
  object: RecordType
  id: string
  people: { id: string; name: string }[]
  /**
   * Overview's uploader, made once by `StandardOverview` and handed to both
   * branches — see `OverviewDrop`. The panel reads `busy`/`progress` for the
   * dashed tile and calls `openPicker`; it does not own the drop.
   */
  uploader: RecordUpload
  /** Anything that changes a count — the first note, the first file. */
  onChanged: () => void
  /** Lends the page the log-activity sheet, for the phone's green button. */
  onLogReady?: (open: (() => void) | null) => void
}

/**
 * The quiet page, top to bottom: a lead-in, the composer, the progress line,
 * the steps, and the ghosts of what will fill in — 14px apart (§4).
 */
export function QuietOverview({
  quiet, object, id, people, uploader, onChanged, onLogReady,
}: QuietOverviewProps) {
  const phone = usePhone()
  const grid = useRef<HTMLDivElement>(null)
  const columns = quietColumns(useBoxWidth(grid))
  const { todo, done, lead, total } = quietProgress(quiet)

  const { post, busy: posting, refresh } = useActivityPost(object, id, onChanged)
  /* The phone's chips offer Email and Text only to somebody with an Inbox (`record-message.tsx`). */
  const canMessage = useCanMessage({ object, id })

  const [logging, setLogging] = useState<ActivityKind | null>(null)
  const [attaching, setAttaching] = useState(false)
  /*
   * A step that aims the composer, and the press that aimed it.
   *
   * The `seq` is the point: pressing **Send a message** twice has to re-aim
   * the composer twice, and an effect keyed on the kind alone would do
   * nothing the second time — which from the outside is a button that works
   * once.
   */
  const [aim, setAim] = useState<{ kind: ActivityKind; seq: number } | null>(null)

  /*
   * The green **Log activity** button in the phone's quick actions opens this
   * sheet rather than navigating to the Activity tab (§4). The page owns that
   * button and the panel owns the sheet, so the opener is lent upwards — the
   * same shape `onUploadReady` already uses for the Files tab's picker.
   */
  const openLog = useCallback(() => setLogging('note'), [])
  useEffect(() => {
    onLogReady?.(openLog)
    /* Handed back on the way out: the panel is gone the moment the first
       note lands, and a button still holding this opener would raise a sheet
       that no longer has anything behind it. */
    return () => onLogReady?.(null)
  }, [onLogReady, openLog])

  /*
   * The files tile's gesture, decided here and nowhere else: a phone has no
   * drop and no file manager worth opening blind, so it gets the three-row
   * sheet (take a photo · library · a file) and a desktop gets the picker.
   */
  const openFiles = useCallback(() => {
    if (phone) setAttaching(true)
    else uploader.openPicker()
  }, [phone, uploader])

  /**
   * What a step actually does — decided here, once, for every layout.
   *
   * The step says what it *is*; the panel is the only thing that knows
   * whether this is a phone, where the file picker lives and where the
   * composer is, and those decide what a tap does. Three answers: the files
   * tile opens a picker or the attach sheet, a composer step aims the
   * composer or opens the log sheet, and everything else goes where it said.
   */
  const take = useCallback((step: QuietStep) => {
    if (step.kind === 'files') { openFiles(); return }
    if (step.composerKind) {
      if (phone) setLogging(step.composerKind)
      else setAim((previous) => ({ kind: step.composerKind!, seq: (previous?.seq ?? 0) + 1 }))
      return
    }
    step.onSelect?.()
  }, [openFiles, phone])

  /*
   * A read-only viewer gets one sentence, not six things to do (§9).
   *
   * §9's own words rather than the config's headline, which is written to
   * lead into a list of things to do — "pick a first step" is the wrong
   * promise to somebody who cannot take one.
   */
  if (!quiet.canWrite) {
    return (
      <section className="rounded-record border border-line bg-surface p-5">
        <h2 className="m-0 text-[19px] font-bold leading-tight tracking-[-0.01em] text-text">
          Nothing has been added to {quiet.composerSubject} yet.
        </h2>
        <p className="m-0 mt-1.5 max-w-[640px] text-[13.5px] leading-[1.5] text-text-muted">
          Notes, jobs and files will show up here.
        </p>
      </section>
    )
  }

  const rows = phone || columns === 1

  return (
    <div className={cn('flex flex-col', phone ? 'gap-3' : 'gap-3.5')}>
      <LeadIn headline={quiet.headline} sub={quiet.sub} />

      {phone
        ? <QuietComposerTrigger subject={quiet.composerSubject} onOpen={setLogging} kinds={messageKinds(canMessage)} />
        : (
          <ActivityComposer
            onSubmit={post}
            people={people}
            busy={posting}
            subject={quiet.composerSubject}
            record={{ object, id }}
            onSent={refresh}
            {...(aim ? { aim } : {})}
          />
        )}

      {uploader.error && (
        <p
          role="alert"
          className="m-0 rounded-record border border-danger-ring bg-danger-bg px-3.5 py-2.5
                     text-body text-danger"
        >
          {uploader.error}
        </p>
      )}

      {/*
        * The rows card carries the progress line in its own 48px header, so
        * drawing it above as well would say "Set up Santos Home · 0 of 6"
        * twice, two inches apart.
        */}
      {!rows && total > 0 && (
        <SetupProgress subject={quiet.setupSubject} done={done.length} total={total} />
      )}

      <div ref={grid}>
        {total === 0
          ? null
          : rows
            ? (
              <QuietRowsCard
                subject={quiet.setupSubject}
                done={done}
                todo={todo}
                lead={lead}
                total={total}
                uploader={uploader}
                onSelect={take}
                phone={phone}
              />
            )
            : (
              <div
                className="grid gap-3"
                style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
              >
                {[...todo, ...done].map((step) => (
                  <QuietStepTile
                    key={step.key}
                    step={step}
                    lead={step === lead}
                    uploader={uploader}
                    onSelect={take}
                  />
                ))}
              </div>
            )}
      </div>

      {quiet.ghosts && quiet.ghosts.length > 0 && (
        <Ghosts ghosts={quiet.ghosts} columns={rows ? 1 : columns} />
      )}

      <QuietLogSheet
        open={logging !== null}
        initialKind={logging ?? 'note'}
        subject={quiet.composerSubject}
        people={people}
        busy={posting}
        onSubmit={post}
        onClose={() => setLogging(null)}
        record={{ object, id }}
        onSent={refresh}
      />

      <AttachSheet
        open={attaching}
        subject={quiet.composerSubject}
        onClose={() => setAttaching(false)}
        onPick={(files, options) => void uploader.add(files, options)}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// The pieces
// ---------------------------------------------------------------------------

function LeadIn({ headline, sub }: { headline: string; sub: string }) {
  return (
    <header className="flex flex-col gap-1.5">
      <h2 className="m-0 text-[19px] font-bold leading-[1.25] tracking-[-0.01em] text-text">
        {headline}
      </h2>
      <p className="m-0 max-w-[640px] text-[13.5px] leading-[1.5] text-text-muted">{sub}</p>
    </header>
  )
}

/**
 * "Set up Santos Home · 2 of 6", and a 96px bar.
 *
 * A real `role="progressbar"` rather than a tinted div (§10): the count is
 * the one thing on this page that says how far along the record is, and a
 * screen reader that cannot read it is left with six unexplained buttons.
 */
export function SetupProgress({ subject, done, total, bar = true }: {
  subject: string
  done: number
  total: number
  /**
   * The 96px track.
   *
   * Dropped in the rows card's own 48px header, where the mockup draws the
   * words alone — but the **role stays either way**, which is why this is one
   * component with a flag rather than two. §10 asks for a `progressbar` with
   * its two numbers, and the layout somebody happens to be on is not a reason
   * for a screen reader to lose the count.
   */
  bar?: boolean
}) {
  return (
    <div
      role="progressbar"
      aria-label={`Set up ${subject}`}
      aria-valuenow={done}
      aria-valuemin={0}
      aria-valuemax={total}
      className="flex items-center gap-2 overflow-hidden"
    >
      <b className="truncate text-[13.5px] font-semibold text-text">Set up {subject}</b>
      <span className="tnum shrink-0 text-cell text-text-faint">
        {bar ? '' : '· '}{done} of {total}
      </span>
      {bar && (
        <span
          aria-hidden
          className="ml-0.5 h-1.5 w-24 shrink-0 overflow-hidden rounded-full border
                     border-line bg-line-faint"
        >
          <span
            className="block h-full rounded-full bg-accent transition-[width]"
            style={{ width: `${total === 0 ? 0 : Math.round((done / total) * 100)}%` }}
          />
        </span>
      )}
    </div>
  )
}

/**
 * One step as a tile — min 108px, padding 14, radius 14 (§6).
 *
 * The lead wears accent-soft with an accent-line border and a solid accent
 * icon tile; **nothing else on the page is green** (§1 rule 3). A done step
 * is transparent, carries a check and is `aria-disabled` rather than
 * `disabled`, so it stays in the tab order and a screen reader can read what
 * was finished instead of skipping it.
 */
export function QuietStepTile({ step, lead, uploader, onSelect }: {
  step: QuietStep
  lead: boolean
  uploader: { over: boolean; busy: boolean; progress: { done: number; total: number } | null }
  /**
   * What pressing it does — the *engine's* answer, not the step's.
   *
   * A step says what it is; the panel knows whether this is a phone, where
   * the picker is and where the composer is, and those are what decide what
   * a tap actually does. `step.onSelect` is the fallback for the ordinary
   * case of going somewhere.
   */
  onSelect: (step: QuietStep) => void
}) {
  const files = step.kind === 'files'
  const done = step.state === 'done'
  const dropping = files && uploader.over

  return (
    <button
      type="button"
      data-quiet-step={step.key}
      data-quiet-lead={lead || undefined}
      aria-disabled={done || undefined}
      onClick={() => { if (!done) onSelect(step) }}
      className={cn(
        'group flex min-h-[108px] items-start gap-3 rounded-record p-3.5 text-left',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        'focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
        done
          ? 'cursor-default border border-transparent'
          : dropping
            ? 'border border-accent-line bg-accent-soft'
            : lead
              ? 'border border-accent-line bg-accent-soft'
              : files
                ? 'border-[1.5px] border-dashed border-line-strong bg-surface-alt'
                : 'border border-line bg-surface hover:border-accent-line',
      )}
    >
      <StepGlyph step={step} lead={lead} done={done} files={files} />

      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <b className={cn('text-[14px] font-semibold leading-tight',
          done ? 'text-text-muted' : lead ? 'text-accent-dark' : 'text-text')}>
          {dropping ? 'Drop to attach' : done ? step.doneTitle ?? step.title : step.title}
        </b>
        <span className={cn('text-body leading-[1.45]',
          lead && !done ? 'text-accent-dark/85' : 'text-text-muted')}>
          {done ? step.doneBody ?? step.body : step.body}
        </span>
        {files && uploader.busy && uploader.progress && (
          <FileProgress progress={uploader.progress} />
        )}
        {done && <span className="sr-only">Done</span>}
      </span>

      {done
        ? null
        : files
          ? (
            <span
              aria-hidden
              className="flex h-7 shrink-0 items-center rounded-button border border-line
                         bg-surface px-[11px] text-cell font-semibold text-text-muted"
            >
              Upload
            </span>
          )
          : (
            <span
              aria-hidden
              className={cn('mt-[3px] shrink-0 transition-transform',
                lead ? 'text-accent' : 'text-text-faint group-hover:text-accent',
                'group-hover:translate-x-0.5')}
            >
              <IconChevronRight size={15} />
            </span>
          )}
    </button>
  )
}

/** The icon square — 40px, or 36 in the Set up card (§6). */
function StepGlyph({ step, lead, done, files, size = 40 }: {
  step: QuietStep
  lead: boolean
  done: boolean
  files: boolean
  size?: 36 | 40
}) {
  const Icon = done ? IconCheck : glyphFor(step.icon)
  return (
    <span
      aria-hidden
      style={{ width: size, height: size }}
      className={cn(
        'flex shrink-0 items-center justify-center rounded-tile-large',
        done
          ? 'bg-hover text-text-faint'
          : lead
            ? 'bg-accent text-surface'
            : files
              ? 'border border-line bg-surface text-text-muted'
              : 'bg-hover text-text-body group-hover:bg-accent-soft group-hover:text-accent',
      )}
    >
      {Icon ? <Icon size={size === 36 ? 18 : 20} /> : null}
    </span>
  )
}

/**
 * A step's glyph, from the three sets a tab names its icon out of.
 *
 * The same lookup order `record-tabs.tsx` uses, for the same reason: a step
 * and the tab it leads to naming one thing with two pictures is how a
 * vocabulary rots.
 */
function glyphFor(name: QuietIcon) {
  if (name in RECORD_TAB_ICONS) return RECORD_TAB_ICONS[name as keyof typeof RECORD_TAB_ICONS]
  if (name in NAV_ICONS) return NAV_ICONS[name as keyof typeof NAV_ICONS]
  return COLUMN_ICONS[name as FieldIcon] ?? null
}

function FileProgress({ progress }: { progress: { done: number; total: number } }) {
  const pct = progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100)
  return (
    <span className="mt-0.5 flex flex-col gap-1">
      <span className="tnum text-chip font-semibold text-text-muted">
        Attaching {progress.total === 1 ? '1 file' : `${progress.total} files`}…
        {' '}{progress.done} of {progress.total}
      </span>
      <span aria-hidden className="h-1 w-full overflow-hidden rounded-full bg-line-faint">
        <span className="block h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
      </span>
    </span>
  )
}

/**
 * The steps as one card of rows — below 560px, and on every phone (§4).
 *
 * A 48px header band on the page background carrying the same progress line
 * the wider layout draws above the grid, then 64px rows. The lead row is
 * accent-soft; there is no second green anywhere.
 */
export function QuietRowsCard({ subject, todo, done, lead, total, uploader, onSelect, phone }: {
  subject: string
  todo: QuietStep[]
  done: QuietStep[]
  lead: QuietStep | null
  total: number
  uploader: { over: boolean; busy: boolean; progress: { done: number; total: number } | null }
  onSelect: (step: QuietStep) => void
  /**
   * Whether this really is a phone, rather than merely narrow.
   *
   * The rows card is the layout below 560px *and* on every phone, which is
   * not the same question as whether a drop is possible: an iPad in portrait
   * draws rows and takes a drop perfectly well. Only the files step's words
   * turn on this, and only because its gesture does.
   */
  phone: boolean
}) {
  return (
    <section className="overflow-hidden rounded-record border border-line bg-surface">
      <header className="flex h-12 items-center border-b border-line bg-bg px-3.5">
        <SetupProgress subject={subject} done={done.length} total={total} bar={false} />
      </header>
      {[...todo, ...done].map((step) => (
        <QuietStepRow
          key={step.key}
          step={step}
          lead={step === lead}
          uploader={uploader}
          onSelect={onSelect}
          phone={phone}
        />
      ))}
    </section>
  )
}

/** One row: a 40px glyph, two lines, a chevron — 36 and 56 in the Set up card. */
export function QuietStepRow({ step, lead, uploader, onSelect, phone, compact }: {
  step: QuietStep
  lead: boolean
  uploader: { over: boolean; busy: boolean; progress: { done: number; total: number } | null }
  /** The engine's answer — see `QuietStepTile`. */
  onSelect: (step: QuietStep) => void
  phone: boolean
  /**
   * The Set up card's shape: 56px with a 36px glyph (§6).
   *
   * Only on a desktop — the same table gives a phone 64 and 40 in both
   * places, because the thing that decides a row's height there is a thumb
   * rather than how important the card is.
   */
  compact?: boolean
}) {
  const files = step.kind === 'files'
  const done = step.state === 'done'
  /* Only the files row changes its words, only on a real phone, and only
     because the gesture does — see `QuietStep.phone`. */
  const words = phone && step.phone ? step.phone : { title: step.title, body: step.body }

  return (
    <button
      type="button"
      data-quiet-step={step.key}
      data-quiet-lead={lead || undefined}
      aria-disabled={done || undefined}
      onClick={() => { if (!done) onSelect(step) }}
      className={cn(
        'flex w-full items-center gap-3 border-t border-line-soft px-3.5 text-left',
        compact ? 'min-h-[56px]' : 'min-h-[64px]',
        'first:border-t-0',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset',
        'focus-visible:ring-accent',
        done ? 'cursor-default' : lead ? 'bg-accent-soft' : 'active:bg-hover',
      )}
    >
      <StepGlyph step={step} lead={lead} done={done} files={files} size={compact ? 36 : 40} />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <b className={cn('truncate font-semibold leading-tight',
          compact ? 'text-body' : 'text-[14.5px]',
          done ? 'text-text-muted' : lead ? 'text-accent-dark' : 'text-text')}>
          {done ? step.doneTitle ?? words.title : words.title}
        </b>
        <span className={cn('truncate text-cell',
          lead && !done ? 'text-accent-dark/85' : 'text-text-muted')}>
          {done ? step.doneBody ?? words.body : words.body}
        </span>
        {files && uploader.busy && uploader.progress && (
          <FileProgress progress={uploader.progress} />
        )}
        {done && <span className="sr-only">Done</span>}
      </span>
      {!done && (
        <span aria-hidden className={cn('shrink-0', lead ? 'text-accent' : 'text-text-faint')}>
          <IconChevronRight size={15} />
        </span>
      )}
    </button>
  )
}

/**
 * What will fill in here — dashed, muted and `aria-hidden` (§4, §10).
 *
 * They are the one thing on this page that is **not** a step: a ghost says
 * "Next up lives here" and does nothing, which is exactly why it is hidden
 * from the accessibility tree rather than offered as a control somebody can
 * press and be puzzled by.
 */
function Ghosts({ ghosts, columns }: {
  ghosts: NonNullable<QuietConfig['ghosts']>
  columns: 1 | 2 | 3
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-cell text-text-faint">What fills in here as you go</span>
      <div
        aria-hidden
        data-quiet-ghosts
        className="grid gap-3"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {ghosts.slice(0, 3).map((ghost) => {
          const Icon = glyphFor(ghost.icon)
          return (
            <div
              key={ghost.title}
              className="flex min-h-[92px] flex-col gap-2 rounded-record border border-dashed
                         border-line p-3 px-3.5"
            >
              <span className="flex items-center gap-1.5 text-[13px] font-semibold text-text-faint">
                {Icon && <span className="text-line-strong"><Icon size={15} /></span>}
                {ghost.title}
              </span>
              <span className="text-cell leading-[1.45] text-text-faint">{ghost.line}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Overview as a drop target — §1 rule 8, §3's last bullet.
 *
 * **The whole tab, not the dashed tile.** The tile only says so; the target
 * is everything, which is why one `useRecordUpload` is made above both
 * branches and this wraps whichever one is drawn. The quiet panel and the
 * busy layout are the same page to somebody holding a file, and a Set up
 * card promising "drop photos, contracts or plans anywhere on this page"
 * over a page that does not take a drop is a lie the card tells on every
 * record that has started filling up.
 *
 * It renders the hidden `<input>`, the overlay and the describe dialog
 * because those belong to the uploader rather than to either layout — the
 * dialog in particular has to outlive a drop that happens while the page is
 * about to flip from quiet to busy.
 */
export function OverviewDrop({ uploader, subject, object, id, onChanged, children }: {
  uploader: RecordUpload
  /** What the overlay says the file is going onto. */
  subject: string
  object: RecordType
  id: string
  onChanged: () => void
  children: ReactNode
}) {
  return (
    <div {...uploader.dropProps} className="relative flex flex-col gap-3.5">
      <input {...uploader.inputProps} />
      {children}
      {uploader.over && <DropOverlay subject={subject} />}

      {uploader.describing && uploader.describing.length > 0 && (
        <DescribeFile
          files={uploader.describing}
          onClose={() => uploader.setDescribing(null)}
          onSave={async (changes) => {
            for (const file of uploader.describing ?? []) {
              await request(`/records/${object}/${id}/files/${file.id}`,
                { method: 'PATCH', body: changes })
            }
            uploader.setDescribing(null)
            onChanged()
          }}
        />
      )}
    </div>
  )
}

/**
 * While a drag is over the page — §9, "Drag over".
 *
 * Over the whole panel rather than on the dashed tile alone, because the drop
 * target *is* the whole panel and an overlay that only lights up one tile
 * teaches the opposite. Announced once through a polite live region, since
 * the visual answer is the only other one there is.
 */
function DropOverlay({ subject }: { subject: string }) {
  return (
    <div
      className="pointer-events-none absolute inset-2 z-10 flex items-center justify-center
                 rounded-record border-2 border-dashed border-accent bg-accent-soft/[0.86]"
    >
      {/*
        * The words sit on a solid pill rather than straight on the tint.
        * 86% of accent-soft is the board's number and it is the right
        * number — the page underneath should still read as the page — but
        * six tile titles showing through one line of 15px type is a line
        * nobody can read, and this is the one moment somebody needs to be
        * sure what is about to happen to the file in their hand.
        */}
      <span
        aria-live="polite"
        className="rounded-record border border-accent-line bg-surface px-4 py-2
                   text-[15px] font-semibold text-accent-dark shadow-popover"
      >
        Drop to attach to {subject}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The Set up card — §3, §4, mockup board 5
// ---------------------------------------------------------------------------

/**
 * How much can be on a record before it stops being prompted.
 *
 * §11's first decision, with its default taken: a record with ten things on
 * it is a record somebody is working, and a card suggesting what to do with
 * it has turned from help into furniture. It is a guess, and it is **one
 * constant in one place** precisely so that changing it is a one-line change
 * rather than an archaeology exercise.
 */
export const SETUP_MAX_THINGS = 10

/** Everything connected to this record — the same counts `isQuietSummary` reads. */
function thingsOn(summary: RecordSummary, related: { area: string }[]): number {
  const counts = summary.counts ?? {}
  return (counts.activity ?? 0) + (counts.files ?? 0)
    + related.reduce((total, spec) => total + (counts[spec.area] ?? 0), 0)
}

/**
 * Does the busy Overview carry a Set up card?
 *
 * Four conditions, and the order they are written in is the order they stop
 * being true in the life of a record: there is a config at all, the viewer
 * may write, they have not hidden it, there is something left to do, and the
 * record is still young enough to be worth prompting.
 */
export function shouldShowSetup(
  quiet: QuietConfig | null | undefined,
  summary: RecordSummary | null,
  related: { area: string }[],
): boolean {
  if (!quiet || !quiet.canWrite || !summary) return false
  if (summary.quiet?.hidden) return false
  if (quietProgress(quiet).todo.length === 0) return false
  return thingsOn(summary, related) <= SETUP_MAX_THINGS
}

/**
 * The steps, after the record has started filling up — board 5, cells 2 and 3.
 *
 * **Only what is left.** The panel shows done steps so a half-set-up record
 * says what is already true; the card does not, because by then the page
 * above it *is* that answer — a Notes card and a Jobs peek are what "you have
 * written a note and made a job" looks like, and a tick beside the same facts
 * is a second, worse copy of them.
 *
 * It goes by itself at `6 of 6`, and `Hide` is the only other way to end it.
 */
export function SetupCard({ quiet, onStep, onHide, phone }: {
  quiet: QuietConfig
  /**
   * What a row does, which the busy Overview decides rather than the panel:
   * there is no composer and no drop overlay here, so the two steps that
   * would have used them go to the tab that has them.
   */
  onStep: (step: QuietStep) => void
  onHide: () => void
  phone: boolean
}) {
  const { todo, done, lead, total } = quietProgress(quiet)
  if (todo.length === 0) return null

  /* No uploader in the card: the drop target is the quiet panel's, and a
     busy Overview has the Files tab two inches above it. */
  const idle = { over: false, busy: false, progress: null }

  return (
    <RecordCard
      title={`Set up ${quiet.setupSubject}`}
      /* The header line board 5 draws, and §10's two numbers in it. */
      countLabel={(
        <span
          role="progressbar"
          aria-label={`Set up ${quiet.setupSubject}`}
          aria-valuenow={done.length}
          aria-valuemin={0}
          aria-valuemax={total}
          className="tnum font-normal text-text-muted"
        >
          {' · '}{done.length} of {total}
        </span>
      )}
      action={{ label: 'Hide', onClick: onHide }}
    >
      {todo.map((step) => (
        <QuietStepRow
          key={step.key}
          step={step}
          lead={step === lead}
          uploader={idle}
          onSelect={onStep}
          phone={phone}
          compact={!phone}
        />
      ))}
    </RecordCard>
  )
}

/**
 * **Show set-up steps**, for the record's ⋯ menu — §4, rule 10.
 *
 * Hiding is quiet and reversible, and this is the reverse. It is a shared
 * helper rather than a line in each record's `menu` so that the words are the
 * same on every record and a page cannot forget the way back: a card somebody
 * dismissed with no way to recall it is a feature they have lost rather than
 * one they have turned off.
 */
export function quietMenuItems(hidden: boolean, onShow: () => void): MenuItem[] {
  return hidden
    ? [{ key: 'show-setup', label: 'Show set-up steps', onSelect: onShow }]
    : []
}

/**
 * Hide or show this record's steps, for this person.
 *
 * `PUT` and idempotent on both sides (migration 0111), because Undo presses
 * it straight back and a toast that fails the second time is worse than one
 * that never offered.
 */
export async function setQuietHidden(
  object: RecordType, id: string, hidden: boolean,
): Promise<void> {
  await request(`/records/${object}/${id}/quiet-hidden`, { method: 'PUT', body: { hidden } })
}
